import { AUTH_ROOT } from 'env'
import React, { useState } from 'react'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Alert from '@material-ui/lab/Alert'
import AccountsBenefitModal from 'components/account-benefits-modal'
import CoachCardSm from 'components/coach-card-small'
import { useStyles } from './styles'
import BookingTypeCard from 'components/booking-type-card'
import PaymentInformation from '../payment-information'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { validationSchema } from '../utils'
import { withStripeElements } from 'lib/stripe'
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { gtmEvent } from 'lib/gtm'
import { useSnackbar } from 'notistack'
import { useMutation } from '@apollo/client'
import { PurchaseLessonPackQuery } from './query'
import CircularProgress from '@material-ui/core/CircularProgress'
import SelectLessonPack from '../components/SelectLessonPack'
import { useLocation } from 'react-router-dom'
import VerifyAccessCode from 'components/verify-access-code'
import BackButton from '../components/BackButton'
import StepDesciptionOfferings from '../step-description-offerings'

const BuyLessonPackLayout = ({
  previousUrl,
  lessonInfo,
  user,
  setIsComplete,
  setLastDigits,
  setCardBrand,
  setSelectedLessonPackId,
  selectedLessonPackId,
  isComplete,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [purchaseLessonPack, { loading }] = useMutation(PurchaseLessonPackQuery)
  const authUrl = `${AUTH_ROOT}/?return_to=${encodeURIComponent(
    window.location.href,
  )}`
  const loginUrl = `${AUTH_ROOT}/login/?return_to=${encodeURIComponent(
    window.location.href,
  )}`
  const {
    coach,
    lessonPacks,
    category,
    title,
    facilities,
    durationInMinutes,
    requiresAccessCode,
  } = lessonInfo
  const facility = facilities[0]
  const profilePhoto = coach?.coachProfile?.profilePhoto
  const form = useForm({
    resolver: yupResolver(validationSchema(true)),
  })
  const stripe = useStripe()
  const elements = useElements()
  const location = useLocation()
  const [verifiedAccessCode, setVerifiedAccessCode] = useState(
    !requiresAccessCode || location?.state?.verifiedAccessCode,
  )

  const onSubmit = () => async formValues => {
    const { error: stripeError, token: stripeToken } = await stripe.createToken(
      elements.getElement(CardNumberElement),
      {
        name: formValues.payment.name,
        address_country: 'US',
      },
    )

    if (stripeError) {
      enqueueSnackbar(stripeError.message, {
        variant: 'error',
      })
      return Promise.resolve()
    }
    return purchaseLessonPack({
      variables: {
        lessonPackId: selectedLessonPackId,
        stripeToken: stripeToken?.id,
      },
    })
      .then(({ data }) => {
        const { success, message } = data.purchaseLessonPack

        if (success) {
          enqueueSnackbar(message, {
            variant: 'success',
          })
          gtmEvent({
            event: 'formSubmit',
            formCategory: 'purchase-lesson-pack',
            formAction: 'clicked-complete-purchase-button',
          })
          setIsComplete(true)
          setLastDigits(stripeToken.card.last4)
          setCardBrand(stripeToken.card.brand)
        } else {
          enqueueSnackbar(message, {
            variant: 'error',
          })
        }
      })
      .catch(error => {
        window.rg4js &&
          window.rg4js('send', {
            error: error,
            tags: ['lesson-pack', 'form-submit'],
          })
        enqueueSnackbar('An error has occurred. Please try again later.', {
          variant: 'error',
        })
      })
  }

  if (loading) {
    return (
      <Container className={classes.root}>
        <div className={classes.loadingStyles}>
          <CircularProgress color="primary" />
        </div>
      </Container>
    )
  }

  return (
    <Container>
      <BackButton previousUrl={previousUrl} />
      <Grid container spacing={2} className={classes.stepContainer}>
        <StepDesciptionOfferings
          title="Buy lesson pack"
          description="Select the lesson pack you'd like to purchase."
        />
      </Grid>
      <Grid container spacing={2}>
        <Grid item md={6} xs={12} className={classes.textWrapped}>
          <CoachCardSm
            coach={{
              name: coach?.name,
              coachAvatar: profilePhoto,
              facilityName: facility?.name,
            }}
          />
          <BookingTypeCard
            category={category}
            facility={facility}
            title={title}
            durationInMinutes={durationInMinutes}
            isComplete={isComplete}
            lessonPacks={lessonPacks}
          />
          <Divider style={{ margin: '8px 0px 40px' }} />
          {user ? (
            <>
              <SelectLessonPack
                lessonPacks={lessonPacks}
                selectedLessonPackId={selectedLessonPackId}
                setSelectedLessonPackId={setSelectedLessonPackId}
              />
              <FormProvider {...form}>
                <form>
                  <Box mb={4}>
                    <PaymentInformation
                      lessonType={lessonInfo}
                      paymentRequired={true}
                    />
                  </Box>
                  <Box mb={4}>
                    <Alert severity="info" className={classes.warningBox}>
                      Your credit card information is not being stored in our
                      system.
                    </Alert>
                  </Box>
                  <Grid item md={5}>
                    <Box mb={4} textAlign="left">
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={form.handleSubmit(onSubmit())}
                        fullWidth
                        disabled={!selectedLessonPackId}
                        data-testid="complete-booking-button"
                      >
                        Complete Purchase
                      </Button>
                    </Box>
                  </Grid>
                </form>
              </FormProvider>
            </>
          ) : (
            <>
              <Typography variant="h5">Sign in or create an account</Typography>
              <Typography variant="body" className={classes.warningBox}>
                You must have a MyPGA account to book this session.{' '}
                <span
                  onClick={() => setIsOpen(true)}
                  className={classes.learnMoreText}
                >
                  Learn more
                </span>
              </Typography>
              <Box display="flex">
                <Grid container spacing={2}>
                  <Grid item md={5}>
                    <Button
                      href={authUrl}
                      variant="contained"
                      color="primary"
                      size="large"
                      style={{ display: 'block', textAlign: 'center' }}
                    >
                      Create an Account
                    </Button>
                  </Grid>
                  <Grid item md={5}>
                    <Button
                      href={loginUrl}
                      variant="outlined"
                      color="primary"
                      size="large"
                      style={{ display: 'block', textAlign: 'center' }}
                    >
                      Sign In
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </Grid>
      </Grid>
      <AccountsBenefitModal isOpen={isOpen} setIsOpen={setIsOpen} />
      <VerifyAccessCode
        lessonType={lessonInfo}
        isOpen={!verifiedAccessCode}
        setIsOpen={setVerifiedAccessCode}
        coachName={coach.name}
        setVerifiedAccessCode={setVerifiedAccessCode}
      />
    </Container>
  )
}

export default withStripeElements(BuyLessonPackLayout)
